import TableInfoDialog from '@/components/orders/tables/delivery-tables/TableInfoDialog';
import { DELIVERY_OPTION, DELIVERY_OPTIONS } from '@montugroup/circuit-shared/constants/shippingOptions';
import { Box, Stack } from '@montugroup/design-system';
import { styled } from '@mui/material';
import type { ComponentProps } from 'react';

type OrderCountBadgeProps = {
  count: number;
};

const OrderCountBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'count',
})<OrderCountBadgeProps>(({ theme, count }) => ({
  height: '1.5rem',
  minWidth: '1.5rem',
  borderRadius: '9999px',
  backgroundColor: theme.palette.primary.contrastText,
  color: theme.palette.secondary.main,
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  lineHeight: 1,
  paddingInline: count > 99 ? '0.4rem' : 0,
  boxSizing: 'border-box',
}));

const TableInfo = {
  [DELIVERY_OPTIONS[DELIVERY_OPTION.SameDayAfternoon].name]: {
    title: 'Same day orders',
    body: (
      <>
        These orders must be dispensed and <b>ready for courier by 12:00 PM today</b>. Ensure all items are packed and
        labelled before the cut-off time.
      </>
    ),
  },
  [DELIVERY_OPTIONS[DELIVERY_OPTION.SameDay].name]: {
    title: 'Same day orders',
    body: (
      <>
        These orders must be dispensed and <b>ready for courier by 3:00 PM today</b>. Ensure all items are packed and
        labelled before the cut-off time.
      </>
    ),
  },
  [DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].name]: {
    title: 'Click and Collect orders',
    body: (
      <>
        For click & collect orders, customer will be notified to come{' '}
        <b>collect order after you mark medication as dispensed</b>.
      </>
    ),
  },
  [DELIVERY_OPTIONS[DELIVERY_OPTION.Standard].name]: {
    title: 'Standard',
    body: (
      <>
        These are routine orders with <b>no urgent time constraints</b>. Prepare and dispense these based on your
        standard fulfillment schedule.
      </>
    ),
  },
};

type Props = {
  table: Exclude<(typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'], 'ThreeHour'>;
  orderCount: number;
  tooltipProps?: ComponentProps<typeof TableInfoDialog>;
};

function OrderTableTitleAdornment({ table, orderCount, tooltipProps }: Props) {
  const tableInfo = TableInfo[table];
  return (
    <Stack direction="row" alignItems="center">
      <TableInfoDialog title={tableInfo.title} body={tableInfo.body} {...tooltipProps} />
      <OrderCountBadge count={orderCount}>{orderCount}</OrderCountBadge>
    </Stack>
  );
}

export default OrderTableTitleAdornment;
